import React, { useState } from 'react';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';

function FbaFeeLookup() {
    const [asin, setAsin] = useState('');
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);

    const handleSearch = async (e) => {
        e.preventDefault();

        if (!asin.trim()) {
            setError('Please enter an ASIN');
            return;
        }

        setLoading(true);
        setError(null);
        setResult(null);

        try {
            const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8080';
            const response = await fetch(`${apiUrl}/api/public/fba-fee?asin=${encodeURIComponent(asin.trim())}`);
            const data = await response.json();

            if (response.ok && data.success) {
                setResult(data);
            } else {
                setError(data.message || 'Failed to retrieve FBA fee data');
            }
        } catch (err) {
            setError('An error occurred while fetching data. Please try again.');
            console.error('Error fetching FBA fee:', err);
        } finally {
            setLoading(false);
        }
    };

    const formatCurrency = (amount, currency = 'USD') => {
        if (!amount) return 'N/A';
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: currency
        }).format(amount);
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
            <div className="container mx-auto px-4 py-12">
                {/* Header */}
                <div className="text-center mb-12">
                    <h1 className="text-4xl font-bold text-gray-900 mb-3">
                        Amazon FBA Fee Lookup
                    </h1>
                    <p className="text-lg text-gray-600">
                        Enter an ASIN to view estimated FBA fulfillment fees
                    </p>
                </div>

                {/* Search Form */}
                <div className="max-w-2xl mx-auto mb-8">
                    <form onSubmit={handleSearch} className="bg-white rounded-lg shadow-lg p-6">
                        <div className="flex gap-3">
                            <input
                                type="text"
                                value={asin}
                                onChange={(e) => setAsin(e.target.value.toUpperCase())}
                                placeholder="Enter ASIN (e.g., B07XYZ1234)"
                                className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-lg"
                                maxLength="10"
                            />
                            <button
                                type="submit"
                                disabled={loading}
                                className="px-8 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 font-semibold"
                            >
                                {loading ? (
                                    <>
                                        <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        Searching...
                                    </>
                                ) : (
                                    <>
                                        <MagnifyingGlassIcon className="h-5 w-5" />
                                        Search
                                    </>
                                )}
                            </button>
                        </div>
                        <p className="text-sm text-gray-500 mt-3">
                            ASIN format: 10 characters starting with B
                        </p>
                    </form>
                </div>

                {/* Error Message */}
                {error && (
                    <div className="max-w-2xl mx-auto mb-8">
                        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                            <div className="flex items-center">
                                <svg className="h-5 w-5 text-red-400 mr-2" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                </svg>
                                <p className="text-red-800 font-medium">{error}</p>
                            </div>
                        </div>
                    </div>
                )}

                {/* Results */}
                {result && (
                    <div className="max-w-4xl mx-auto">
                        <div className="bg-white rounded-lg shadow-xl overflow-hidden">
                            {/* Product Header */}
                            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-4">
                                <h2 className="text-2xl font-bold text-white">Fee Information</h2>
                            </div>

                            <div className="p-8">
                                {/* Product Info Section */}
                                <div className="flex gap-6 mb-8 pb-8 border-b border-gray-200">
                                    {/* Product Image */}
                                    {result.image_url && (
                                        <div className="flex-shrink-0">
                                            <img
                                                src={result.image_url}
                                                alt={result.product_title || 'Product'}
                                                className="w-32 h-32 object-contain rounded-lg border border-gray-200"
                                                onError={(e) => {
                                                    e.target.style.display = 'none';
                                                }}
                                            />
                                        </div>
                                    )}

                                    {/* Product Details */}
                                    <div className="flex-1">
                                        <h3 className="text-xl font-semibold text-gray-900 mb-2">
                                            {result.product_title || 'Product Title Not Available'}
                                        </h3>
                                        <div className="space-y-1 text-sm text-gray-600">
                                            <p><span className="font-medium">ASIN:</span> {result.asin}</p>
                                            {result.seller_sku && (
                                                <p><span className="font-medium">SKU:</span> {result.seller_sku}</p>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Fee Information Grid */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                                    {/* FBA Fee - Prominent Display */}
                                    <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg p-6 border border-green-200">
                                        <p className="text-sm font-medium text-gray-600 mb-1">FBA Fulfillment Fee</p>
                                        <p className="text-4xl font-bold text-green-600">
                                            {formatCurrency(result.fee_amount, result.currency)}
                                        </p>
                                    </div>

                                    {/* Item Price */}
                                    {result.item_price && (
                                        <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
                                            <p className="text-sm font-medium text-gray-600 mb-1">Reference Item Price</p>
                                            <p className="text-4xl font-bold text-gray-900">
                                                {formatCurrency(result.item_price, result.currency)}
                                            </p>
                                        </div>
                                    )}
                                </div>

                                {/* Additional Info */}
                                <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                                    <div className="flex items-start">
                                        <svg className="h-5 w-5 text-blue-600 mr-2 mt-0.5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                                        </svg>
                                        <div className="flex-1">
                                            <p className="text-sm text-blue-900">
                                                <span className="font-medium">Last Updated:</span> {formatDate(result.last_updated)}
                                            </p>
                                            <p className="text-xs text-blue-700 mt-1">
                                                FBA fees are estimates and may vary based on product dimensions, weight, and category.
                                                This data is cached and may not reflect the most current Amazon fee structure.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Footer Info */}
                {!result && !error && !loading && (
                    <div className="max-w-2xl mx-auto mt-12">
                        <div className="bg-white rounded-lg shadow-md p-6">
                            <h3 className="text-lg font-semibold text-gray-900 mb-3">About FBA Fees</h3>
                            <ul className="space-y-2 text-sm text-gray-600">
                                <li className="flex items-start">
                                    <svg className="h-5 w-5 text-green-500 mr-2 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                    </svg>
                                    FBA fees cover picking, packing, shipping, customer service, and returns
                                </li>
                                <li className="flex items-start">
                                    <svg className="h-5 w-5 text-green-500 mr-2 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                    </svg>
                                    Fees vary based on product size, weight, and category
                                </li>
                                <li className="flex items-start">
                                    <svg className="h-5 w-5 text-green-500 mr-2 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                    </svg>
                                    Additional storage fees may apply for long-term inventory
                                </li>
                            </ul>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default FbaFeeLookup;
